import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Avatar,
  Grid,
  FormControl,
  Select,
  MenuItem,
  alpha,
  useTheme
} from '@mui/material';
import Settings from '@mui/icons-material/Settings';
import { formatProduct } from '../../../utils/utilfunction';

const SettingsDialog = ({
  open,
  onClose,
  orderType,
  setOrderType,
  productType,
  setProductType,
  onSave,
  isUpdating,
  updateError
}) => {
  const theme = useTheme();

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="sm" 
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 4,
          boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
          backgroundImage: `linear-gradient(to bottom right, ${alpha(theme.palette.background.paper, 1)}, ${alpha(theme.palette.background.paper, 0.95)})`,
          overflow: 'hidden'
        }
      }}
    >
      <Box sx={{ 
        p: 3.5, 
        background: theme.palette.mode === 'dark'
          ? `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`
          : `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        position: 'relative',
        overflow: 'hidden',
        '&::after': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: 'radial-gradient(circle at 20px 20px, rgba(255,255,255,0.15) 2px, transparent 0)',
          backgroundSize: '40px 40px',
          opacity: 0.5
        }
      }}>
        <Avatar sx={{ 
          bgcolor: 'rgba(255,255,255,0.25)',
          width: 52,
          height: 52,
          boxShadow: '0 4px 10px rgba(0,0,0,0.2)'
        }}>
          <Settings sx={{ color: 'white', fontSize: '1.8rem' }} />
        </Avatar>
        <Typography variant="h5" sx={{ color: 'white', fontWeight: 700, textShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
          Edit Trading Settings
        </Typography>
      </Box>

      <DialogContent sx={{ py: 4.5, px: 4 }}>
        {updateError && (
          <Box sx={{ 
            mb: 3, 
            p: 2, 
            borderRadius: 2, 
            bgcolor: alpha(theme.palette.error.main, 0.1),
            border: `1px solid ${alpha(theme.palette.error.main, 0.3)}`,
            color: theme.palette.error.main
          }}>
            <Typography variant="body2">{updateError}</Typography>
          </Box>
        )}
        <Grid container spacing={4}>
          <Grid item xs={12} md={6}>
            <Box sx={{ mb: 1.5 }}>
              <Typography variant="subtitle1" sx={{ 
                fontWeight: 700, 
                mb: 1, 
                display: 'flex', 
                alignItems: 'center', 
                gap: 1.2, 
                fontSize: '1.05rem' 
              }}>
                <Box component="span" sx={{ 
                  width: 12, 
                  height: 12, 
                  borderRadius: '50%', 
                  bgcolor: theme.palette.primary.main,
                  display: 'inline-block',
                  boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.2)}`
                }} />
                Order Type
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2, pl: 3.2 }}>
                Default order type for placing new trades
              </Typography>
            </Box>
            <FormControl fullWidth variant="outlined">
              <Select
                value={orderType}
                onChange={(e) => setOrderType(e.target.value)}
                sx={{ 
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: alpha(theme.palette.primary.main, 0.3),
                    borderWidth: 2
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: alpha(theme.palette.primary.main, 0.5),
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.palette.primary.main,
                  },
                  borderRadius: 2.5,
                  '.MuiSelect-select': {
                    py: 1.5
                  }
                }}
              >
                <MenuItem value="LIMIT">LIMIT</MenuItem>
                <MenuItem value="MARKET">MARKET</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Box sx={{ mb: 1.5 }}>
              <Typography variant="subtitle1" sx={{ 
                fontWeight: 700, 
                mb: 1, 
                display: 'flex', 
                alignItems: 'center', 
                gap: 1.2, 
                fontSize: '1.05rem' 
              }}>
                <Box component="span" sx={{ 
                  width: 12, 
                  height: 12, 
                  borderRadius: '50%', 
                  bgcolor: theme.palette.secondary.main,
                  display: 'inline-block',
                  boxShadow: `0 0 0 3px ${alpha(theme.palette.secondary.main, 0.2)}`
                }} />
                Product Type
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2, pl: 3.2 }}>
                Default product type for placing new trades
              </Typography>
            </Box>
            <FormControl fullWidth variant="outlined">
              <Select
                value={productType}
                onChange={(e) => setProductType(e.target.value)}
                sx={{ 
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: alpha(theme.palette.secondary.main, 0.3),
                    borderWidth: 2
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: alpha(theme.palette.secondary.main, 0.5),
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.palette.secondary.main,
                  },
                  borderRadius: 2.5,
                  '.MuiSelect-select': {
                    py: 1.5
                  }
                }}
              >
                <MenuItem value="CNC/NRML">{formatProduct("CNC/NRML")}</MenuItem>
                <MenuItem value="MIS">{formatProduct("MIS")}</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ px: 4, pb: 4, pt: 2, gap: 2 }}>
        <Button 
          onClick={onClose}
          variant="outlined"
          sx={{ 
            borderRadius: 8, 
            textTransform: 'none', 
            fontWeight: 600,
            px: 3,
            py: 1.2,
            borderColor: alpha(theme.palette.text.primary, 0.2),
            color: theme.palette.text.primary,
            '&:hover': {
              borderColor: alpha(theme.palette.text.primary, 0.5),
              backgroundColor: alpha(theme.palette.text.primary, 0.03),
            }
          }}
        >
          Cancel
        </Button>
        <Button 
          onClick={onSave}
          variant="contained"
          disabled={isUpdating}
          sx={{ 
            borderRadius: 8, 
            textTransform: 'none', 
            fontWeight: 600,
            px: 4,
            py: 1.2,
            boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.25)}`,
            background: theme.palette.mode === 'dark'
              ? `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`
              : `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
            '&:hover': {
              boxShadow: `0 6px 16px ${alpha(theme.palette.primary.main, 0.35)}`,
              background: theme.palette.mode === 'dark'
                ? `linear-gradient(90deg, ${theme.palette.primary.dark}, ${theme.palette.secondary.dark})`
                : `linear-gradient(90deg, ${theme.palette.primary.dark}, ${theme.palette.secondary.dark})`,
            },
            '&.Mui-disabled': {
              background: theme.palette.action.disabledBackground,
              color: theme.palette.action.disabled
            }
          }}
          startIcon={isUpdating ? <Box sx={{ 
            width: 16, 
            height: 16, 
            borderRadius: '50%', 
            border: '2px solid currentColor',
            borderTopColor: 'transparent',
            animation: 'spin 1s linear infinite',
            '@keyframes spin': {
              '0%': { transform: 'rotate(0deg)' },
              '100%': { transform: 'rotate(360deg)' }
            }
          }} /> : null}
        >
          {isUpdating ? 'Saving...' : 'Save Changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SettingsDialog; 